import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { Subscription, fromEvent, timer } from 'rxjs';
import {
  filter,
  debounceTime,
  switchMap,
  take,
  distinctUntilChanged,
  map,
  finalize,
} from 'rxjs/operators';
import { DomainService } from '../../domain/domain.service';
import { AuthService } from '../../guards/service/auth.service';
import { FreelancerDataService } from '../../../services/freelancer-data.service';
import { ToastrService } from 'ngx-toastr';
import { LayoutService } from '../../layout/layout.service';

@Component({
  selector: 'app-side-nav',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.css'],
})
export class SideNavComponent implements OnInit, OnDestroy {
  readonly FALLBACK_AVATAR =
    'https://kontact-app-public.s3-ap-southeast-1.amazonaws.com/images/Sign-Up-Image.jpg';

  spaceID = 0;
  profileDetails: any = null;
  designation = '';
  profilePhoto = this.FALLBACK_AVATAR;
  userCommunities: any[] = [];
  currentSlug = '';
  currentCommunityId = '';

  isSwitching = false;

  private subs = new Subscription();
  private _loadedOnce = false;
  private _lastBoundSpaceId = 0;
  private _lastBoundUserId: number | null = null;
  private _requestVersion = 0;

  private fetching = {
    communities: false,
    avatar: false,
    info: false,
    desig: false,
  };

  constructor(
    private authService: AuthService,
    private dataService: FreelancerDataService,
    public domainService: DomainService,
    private toastr: ToastrService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    public layout: LayoutService
  ) {}

  ngOnInit(): void {
    if (this.isPublicCommunityPage()) return;

    this.subs.add(
      this.domainService.spaceID$
        .pipe(
          filter((sid): sid is number => !!sid),
          distinctUntilChanged()
        )
        .subscribe(() => this.initWithContext('spaceID$'))
    );

    this.subs.add(
      this.router.events
        .pipe(
          filter((e) => e instanceof NavigationEnd),
          debounceTime(30),
          map(() => ({
            slug: this.domainService.getSlugSync(),
            cid: this.domainService.getCommunityIdSync(),
          })),
          distinctUntilChanged(
            (a, b) => a.slug === b.slug && a.cid === b.cid
          )
        )
        .subscribe(() => {
          this.syncRouteContext();

          if (
            this.domainService.isCommunityDomain() &&
            this.spaceID &&
            !this.userCommunities?.length
          ) {
            this.getUserCommunities(false);
          }
        })
    );

    this.subs.add(
      this.authService.authChanged$.pipe(debounceTime(30)).subscribe(() => {
        if (!this.authService.isLoggedIn()) {
          this.resetSideNavState();
          return;
        }

        this.resetSideNavState();
        this.syncRouteContext();
        this.initWithContext('authChanged$');
      })
    );

    if (typeof window !== 'undefined') {
      this.subs.add(
        fromEvent<StorageEvent>(window, 'storage')
          .pipe(debounceTime(30))
          .subscribe((ev) => {
            if (!ev?.key) return;

            if (['slug', 'communityId', 'spaceID'].includes(ev.key)) {
              const slug = localStorage.getItem('slug') || '';
              const cid =
                Number(localStorage.getItem('communityId') || '0') || null;
              const sid =
                Number(localStorage.getItem('spaceID') || '0') || null;

              if (slug && cid) {
                this.domainService.setContext(slug, cid, sid || undefined);
              }
            }
          })
      );
    }

    this.syncRouteContext();

    if (this.domainService.isContextReadySync()) {
      this.initWithContext('initSync');
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private initWithContext(reason: string): void {
    const sid =
      this.domainService.getSpaceIdSync() ||
      Number(localStorage.getItem('spaceID') || '0') ||
      0;

    const userId = this.authService.getUserId();

    const slug =
      this.domainService.getSlugSync() || localStorage.getItem('slug') || '';

    const cid = String(
      this.domainService.getCommunityIdSync() ||
        localStorage.getItem('communityId') ||
        ''
    );

    if (!sid || !userId) return;

    if (
      this._lastBoundSpaceId === sid &&
      this._lastBoundUserId === userId &&
      this._loadedOnce
    ) {
      return;
    }

    this._requestVersion++;

    this.spaceID = sid;
    this._lastBoundSpaceId = sid;
    this._lastBoundUserId = userId;
    this.currentSlug = slug;
    this.currentCommunityId = cid;

    this.profileDetails = null;
    this.designation = '';
    this.profilePhoto = this.FALLBACK_AVATAR;
    this.userCommunities = [];

    this._loadedOnce = false;

    if (this.domainService.isCommunityDomain()) {
      this.getUserCommunities(true);
    } else {
      this.loadProfileData();
      this._loadedOnce = true;
    }

    this.cdr.markForCheck();
  }

  private syncRouteContext(): void {
    const slug =
      this.domainService.getSlugSync() || localStorage.getItem('slug') || '';
    const cid =
      this.domainService.getCommunityIdSync() ||
      localStorage.getItem('communityId') ||
      '';

    if (slug) this.currentSlug = slug;
    if (cid) this.currentCommunityId = String(cid);
  }

  private isPublicCommunityPage(): boolean {
    if (!this.domainService.isCommunityDomain()) return false;

    const url = this.router.url.toLowerCase();
    const segments = url.split('?')[0].split('/').filter(Boolean);

    if (segments.length === 1) return true;
    if (/\/login($|\?)/.test(url)) return true;
    if (/\/registration(\/|$|\?)/.test(url)) return true;
    if (/\/forgot-password(\/|$|\?)/.test(url)) return true;
    if (/\/reset-password(\/|$|\?)/.test(url)) return true;

    return false;
  }

  goToProfile(): void {
    const slug = this.domainService.getSlugSync() || localStorage.getItem('slug');
    const cid =
      this.domainService.getCommunityIdSync() ||
      localStorage.getItem('communityId');

    if (slug && cid) {
      this.router.navigate(['/', slug, cid, 'profile']);
      return;
    }

    this.router.navigate(['/profile']);
  }

  private loadProfileData(): void {
    if (!this.spaceID) return;

    this.profilePhoto = this.FALLBACK_AVATAR;

    this.getFreelancerAvatar();
    this.getFreelancerPersonalInfo();
    this.getFreelancerDesignation();
  }

  private getFreelancerPersonalInfo(): void {
    if (!this.spaceID || this.fetching.info) return;

    const requestVersion = this._requestVersion;
    const requestSpaceId = this.spaceID;
    const requestUserId = this.authService.getUserId();

    this.fetching.info = true;

    this.dataService
      .getFreelancerProfile({}, requestSpaceId)
      .pipe(finalize(() => (this.fetching.info = false)))
      .subscribe({
        next: (res) => {
          if (
            requestVersion !== this._requestVersion ||
            requestSpaceId !== this.spaceID ||
            requestUserId !== this.authService.getUserId()
          ) {
            return;
          }

          this.profileDetails = res?.data ?? null;
          this.cdr.markForCheck();
        },
        error: () => this.cdr.markForCheck(),
      });
  }

  private getUserCommunities(reloadProfileAfterBind = false): void {
    if (!this.spaceID || this.fetching.communities) return;

    const requestVersion = this._requestVersion;
    const requestSpaceId = this.spaceID;
    const requestUserId = this.authService.getUserId();

    this.fetching.communities = true;

    this.dataService
      .getUserCommunities({}, requestSpaceId)
      .pipe(finalize(() => (this.fetching.communities = false)))
      .subscribe({
        next: (res) => {
          if (
            requestVersion !== this._requestVersion ||
            requestSpaceId !== this.spaceID ||
            requestUserId !== this.authService.getUserId()
          ) {
            return;
          }

          const list = Array.isArray(res?.data) ? res.data : [];
          this.userCommunities = list;
          this.cdr.markForCheck();

          const cid = Number(this.currentCommunityId || '0');
          const slug = (this.currentSlug || '').trim();

          if (slug && cid && this.userCommunities.length) {
            const match = this.userCommunities.find(
              (c: any) => Number(c.id) === cid
            );

            if (!match) {
              this.toastr.error('You are not a member of this community.');
              this.router.navigate([`/${slug}/${cid}/login`]);
              return;
            }

            const targetSpaceId = Number(match?.Spaces?.id || 0);

            if (targetSpaceId && targetSpaceId !== this.spaceID) {
              this.profilePhoto = this.FALLBACK_AVATAR;
              this.rebindToSpace(targetSpaceId, cid, slug);

              if (reloadProfileAfterBind) {
                this.loadProfileData();
                this._loadedOnce = true;
              }

              return;
            }
          }

          if (
            (reloadProfileAfterBind || !this._loadedOnce) &&
            !this.loadingProfile()
          ) {
            this.loadProfileData();
            this._loadedOnce = true;
          }

          if (!list.length) {
            this.subs.add(
              timer(250)
                .pipe(
                  take(1),
                  switchMap(() =>
                    this.dataService.getUserCommunities({}, this.spaceID)
                  )
                )
                .subscribe({
                  next: (again) => {
                    if (
                      requestVersion !== this._requestVersion ||
                      requestSpaceId !== this.spaceID ||
                      requestUserId !== this.authService.getUserId()
                    ) {
                      return;
                    }

                    const l2 = Array.isArray(again?.data) ? again.data : [];

                    if (l2.length) {
                      this.userCommunities = l2;
                      this.cdr.markForCheck();
                    }
                  },
                })
            );
          }
        },
        error: () => {
          this.cdr.markForCheck();
        },
      });
  }

  private loadingProfile(): boolean {
    return this.fetching.avatar || this.fetching.info || this.fetching.desig;
  }

  private rebindToSpace(
    spaceID: number,
    communityId: number,
    slug: string
  ): void {
    this._requestVersion++;

    this.spaceID = spaceID;
    this._lastBoundSpaceId = spaceID;

    localStorage.setItem('spaceID', String(spaceID));
    localStorage.setItem('communityId', String(communityId));
    localStorage.setItem('slug', slug);

    this.domainService.setContext(slug, communityId, spaceID);
  }

  private getFreelancerAvatar(): void {
    if (!this.spaceID || this.fetching.avatar) return;

    const requestVersion = this._requestVersion;
    const requestSpaceId = this.spaceID;
    const requestUserId = this.authService.getUserId();

    this.fetching.avatar = true;
    this.profilePhoto = this.FALLBACK_AVATAR;

    this.dataService
      .getFreelancerAvatar({}, requestSpaceId)
      .pipe(finalize(() => (this.fetching.avatar = false)))
      .subscribe({
        next: (res) => {
          if (
            requestVersion !== this._requestVersion ||
            requestSpaceId !== this.spaceID ||
            requestUserId !== this.authService.getUserId()
          ) {
            return;
          }

          const raw = this.pickAvatarUrl(res);
          const normalized = raw
            ? this.isDataUrl(raw)
              ? raw.trim()
              : this.withCacheBuster(this.ensureHttpProtocol(raw.trim()))
            : '';

          this.profilePhoto = normalized || this.FALLBACK_AVATAR;
          this.cdr.markForCheck();
        },
        error: () => {
          this.profilePhoto = this.FALLBACK_AVATAR;
          this.cdr.markForCheck();
        },
      });
  }

  private getFreelancerDesignation(): void {
    if (!this.spaceID || this.fetching.desig) return;

    const requestVersion = this._requestVersion;
    const requestSpaceId = this.spaceID;
    const requestUserId = this.authService.getUserId();

    this.fetching.desig = true;

    this.dataService
      .getFreelancerDesignations({}, requestSpaceId)
      .pipe(finalize(() => (this.fetching.desig = false)))
      .subscribe({
        next: (res) => {
          if (
            requestVersion !== this._requestVersion ||
            requestSpaceId !== this.spaceID ||
            requestUserId !== this.authService.getUserId()
          ) {
            return;
          }

          this.designation = res?.data?.[0]?.designation || '';
          this.cdr.markForCheck();
        },
        error: () => this.cdr.markForCheck(),
      });
  }

  onCommunityChange(community: any): void {
    if (this.isSwitching) return;

    const userId = this.authService.getUserId();

    if (!this.authService.isLoggedIn() || !userId) return;

    const targetSpace = Number(community?.Spaces?.id || 0);
    const targetCid = Number(community?.id || 0);
    const targetSlug = this.toSlug(String(community?.community_url ?? ''));

    if (!targetSpace || !targetCid || !targetSlug) {
      this.toastr.error('Community is missing identifiers.');
      return;
    }

    if (
      String(targetCid) === this.currentCommunityId &&
      targetSlug === this.currentSlug
    ) {
      return;
    }

    this.isSwitching = true;
    this._requestVersion++;

    this.profileDetails = null;
    this.designation = '';
    this.profilePhoto = this.FALLBACK_AVATAR;

    sessionStorage.setItem('switchingCommunity', '1');
    sessionStorage.setItem('pendingSpaceID', String(targetSpace));

    this.dataService
      .switchCommunityToken({
        user_id: +userId,
        space_id: +targetSpace,
      })
      .subscribe({
        next: (res) => {
          const { token, space_id } = res?.data || {};

          sessionStorage.removeItem('pendingSpaceID');

          if (!token || !space_id) {
            this.cleanupSwitch();
            this.toastr.error('Failed to receive new token.');
            return;
          }

          this.authService.setToken(token);

          localStorage.setItem('spaceID', String(space_id));
          localStorage.setItem('communityId', String(targetCid));
          localStorage.setItem('slug', targetSlug);

          this.spaceID = Number(space_id);
          this.currentCommunityId = String(targetCid);
          this.currentSlug = targetSlug;

          this._lastBoundSpaceId = Number(space_id);
          this._lastBoundUserId = this.authService.getUserId();
          this._loadedOnce = false;

          this.domainService.setContext(targetSlug, targetCid, Number(space_id));

          this.loadProfileData();
          this._loadedOnce = true;

          this.router
            .navigateByUrl(`/${targetSlug}/${targetCid}/home`, {
              replaceUrl: true,
            })
            .finally(() => this.cleanupSwitch());
        },
        error: () => {
          this.cleanupSwitch();
          this.toastr.error('Failed to switch community');
        },
      });
  }

  private cleanupSwitch(): void {
    this.isSwitching = false;

    sessionStorage.removeItem('switchingCommunity');
    sessionStorage.removeItem('pendingSpaceID');

    this.layout.reinitLayout?.();
  }

  private toSlug(u: string): string {
    const raw = (u || '').trim();

    try {
      const url = new URL(raw);
      return url.pathname.replace(/^\/+|\/+$/g, '');
    } catch {
      return raw.replace(/^\/+|\/+$/g, '');
    }
  }

  onLogout(): void {
    this.resetSideNavState();
    this.authService.handleLogout();
  }

  private isDataUrl(u: string): boolean {
    return /^data:image\/[a-zA-Z0-9.+-]+;base64,/.test((u || '').trim());
  }

  private ensureHttpProtocol(u: string): string {
    const s = (u || '').trim();

    if (this.isDataUrl(s)) return s;
    if (/^\/\//.test(s)) return 'https:' + s;

    return s;
  }

  private withCacheBuster(u: string): string {
    if (!u) return this.FALLBACK_AVATAR;

    const sep = u.includes('?') ? '&' : '?';
    return `${u}${sep}v=${Date.now()}`;
  }

  private pickAvatarUrl(res: any): string | null {
    const candidates: any[] = [
      res,
      res?.data,
      res?.data?.url,
      res?.data?.avatar,
      res?.url,
      res?.avatar,
      Array.isArray(res?.data) ? res.data[0] : null,
    ].filter(Boolean);

    for (const c of candidates) {
      if (typeof c === 'string' && c.trim()) {
        return c.trim();
      }

      if (typeof c === 'object') {
        const maybe =
          (c as any)?.url ??
          (c as any)?.avatar ??
          (Array.isArray(c) ? c[0] : null);

        if (typeof maybe === 'string' && maybe.trim()) {
          return maybe.trim();
        }
      }
    }

    return null;
  }

  private resetSideNavState(): void {
    this._requestVersion++;

    this.spaceID = 0;
    this.profileDetails = null;
    this.designation = '';
    this.profilePhoto = this.FALLBACK_AVATAR;
    this.userCommunities = [];
    this.currentSlug = '';
    this.currentCommunityId = '';

    this._loadedOnce = false;
    this._lastBoundSpaceId = 0;
    this._lastBoundUserId = null;

    this.fetching = {
      communities: false,
      avatar: false,
      info: false,
      desig: false,
    };

    this.cdr.markForCheck();
  }
}